import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faCopy } from '@fortawesome/free-solid-svg-icons';
import { HeaderLogo } from './Brand';

const LogIn = ({
  entity,
  handleSubmit,
  setStep,
  prevStep,
  practiceFusionOtp,
  hideHeader,
  variant,
}) => {
  const [credentials, setCredentials] = useState({
    email: '',
    username: '',
    password: '',
    domain: '',
    accountNumber: '',
  });
  const [emailValid, setEmailValid] = useState(true);
  const [touched, setTouched] = useState(false);
  const [otpCopied, setOtpCopied] = useState(false);
  const [otpEnteredConfirmed, setOtpEnteredConfirmed] = useState(false);
  const usernameBasedEmrs = [
    'Waystar',
    'PTPracticePro',
    'Jane',
    'DrChrono',
    'Athena',
    'Modmed',
    'ModmedGastro',
    'eClinicalWorks',
    'WebPt',
    'Raintree',
    'Experity',
    'EZDERM',
    'Credible',
    'ModmedEMA',
  ];
  const entityName = entity.name || entity.emrName;

  // Nextech orgs on a custom *.nextech.com domain (anything other than the shared
  // login.nextech.com flow) log in via a single-page form that requires the
  // practice Account Number. Default-domain orgs use the Auth0 flow with no
  // Account Number, so we only collect it for custom-domain orgs.
  // NextechPM (Practice+) orgs with no domain default to pm.nextech.com, which
  // serves the same classic form, so they need the Account Number too.
  const usesUniversalLogin = (entity.emrInstanceDomain || '').includes(
    'login.nextech.com',
  );
  const requiresNextechAccountNumber =
    (entityName === 'Nextech' &&
      !!entity.emrInstanceDomain &&
      !usesUniversalLogin) ||
    (entityName === 'NextechPM' && !usesUniversalLogin);

  const checkEmailValid = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCredentials((prev) => ({ ...prev, [name]: value }));
    if (name === 'email') {
      if (touched) setEmailValid(checkEmailValid(value));
    }
  };

  const handleEmailBlur = () => {
    setTouched(true);
    setEmailValid(checkEmailValid(credentials.email));
  };

  const checkFormValid = () => {
    const basicValidation = usernameBasedEmrs.includes(entityName)
      ? credentials.username.length > 0 && credentials.password.length > 0
      : emailValid &&
        credentials.email.length > 0 &&
        credentials.password.length > 0;

    if (entityName === 'Credible') {
      return basicValidation && credentials.domain.length > 0;
    }

    // Custom-domain Nextech orgs require the practice Account Number.
    if (requiresNextechAccountNumber) {
      return basicValidation && credentials.accountNumber.length > 0;
    }

    // For Practice Fusion with OTP, also require OTP confirmation
    if (entityName === 'PracticeFusion' && practiceFusionOtp) {
      return basicValidation && otpEnteredConfirmed;
    }

    return basicValidation;
  };

  const handleSubmission = () => {
    if (checkFormValid()) {
      const identity = usernameBasedEmrs.includes(entityName)
        ? credentials.username
        : credentials.email;
      // The third argument is persisted as the org's domain_name. Custom-domain
      // Nextech sends its practice Account Number; Credible (and others) send
      // their domain code.
      const orgIdentifier = requiresNextechAccountNumber
        ? credentials.accountNumber
        : credentials.domain;
      handleSubmit(identity, credentials.password, orgIdentifier);
    }
  };

  const handleCopyOtp = async () => {
    if (practiceFusionOtp) {
      try {
        await navigator.clipboard.writeText(practiceFusionOtp);
        setOtpCopied(true);
        setTimeout(() => setOtpCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy OTP:', err);
      }
    }
  };

  const getImageHeightClass = (entityName) => {
    switch (entityName) {
      case 'OncoEMR':
        return 'cobalt-h-16 cobalt-w-auto';
      case 'eClinicalWorks':
        return 'cobalt-h-7 cobalt-w-auto';
      case 'Jane':
        return 'cobalt-h-24 cobalt-w-auto';
      default:
        return 'cobalt-h-10 cobalt-w-auto';
    }
  };

  return (
    <>
      {!hideHeader && (
        <div
          className={`cobalt-flex cobalt-items-center cobalt-justify-between cobalt-p-4`}
        >
          <button
            onClick={() => setStep(prevStep)}
            className="cobalt-text-gray-500"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
          <HeaderLogo variant={variant} size={24} />
        </div>
      )}

      <div
        className={`cobalt-flex cobalt-flex-col cobalt-items-start cobalt-mt-12 cobalt-mb-4`}
      >
        <div
          className={`cobalt-flex cobalt-flex-row cobalt-items-start cobalt-mb-12`}
        >
          <div
            className="cobalt-flex-shrink-0 cobalt-mr-2"
            style={{ display: 'flex', alignItems: 'center' }}
          >
            <img
              src={entity.logoUrl || entity.emrLogoUrl}
              alt={`${entityName} Logo`}
              className={`cobalt-object-contain ${getImageHeightClass(entityName)}`}
              style={{ maxWidth: '100%' }}
            />
          </div>
        </div>
        <h1 className="cobalt-text-left cobalt-font-normal">
          Log into {entityName}
        </h1>
      </div>
      <div className="cobalt-flex cobalt-flex-col cobalt-items-centers">
        {entityName === 'PracticeFusion' && practiceFusionOtp && (
          <>
            <div className="cobalt-mb-4 cobalt-p-3 cobalt-bg-green-50 cobalt-border-l-4 cobalt-border-green-400 cobalt-rounded-r">
              <p className="cobalt-text-xs cobalt-text-green-800 cobalt-leading-relaxed cobalt-mb-3">
                <span className="cobalt-font-semibold">Step 2:</span> Use this
                one-time password in Practice Fusion:
              </p>
              <div className="cobalt-bg-white cobalt-p-3 cobalt-rounded cobalt-border cobalt-mb-3">
                <div className="cobalt-flex cobalt-items-center cobalt-justify-center cobalt-space-x-3">
                  <span className="cobalt-font-mono cobalt-text-xl cobalt-font-bold cobalt-text-green-700">
                    {practiceFusionOtp}
                  </span>
                  <button
                    onClick={handleCopyOtp}
                    className={`cobalt-flex cobalt-items-center cobalt-justify-center cobalt-w-8 cobalt-h-8 cobalt-rounded cobalt-transition-colors cobalt-duration-200 ${
                      otpCopied
                        ? 'cobalt-bg-green-100 cobalt-text-green-600'
                        : 'cobalt-text-primary hover:cobalt-bg-gray-100'
                    }`}
                    style={!otpCopied ? { color: '#576de' } : {}}
                    title={otpCopied ? 'Copied!' : 'Copy OTP'}
                  >
                    <FontAwesomeIcon icon={faCopy} />
                  </button>
                </div>
              </div>
              <p className="cobalt-text-xs cobalt-text-green-700 cobalt-leading-relaxed">
                Enter this code in Practice Fusion and log in there.
              </p>
            </div>
            <div className="cobalt-flex cobalt-items-center cobalt-mb-4 cobalt-w-full">
              <input
                type="checkbox"
                id="otpEnteredConfirmed"
                checked={otpEnteredConfirmed}
                onChange={(e) => setOtpEnteredConfirmed(e.target.checked)}
                className="cobalt-mr-3 cobalt-w-4 cobalt-h-4 cobalt-text-primary cobalt-border-gray-300 cobalt-rounded"
              />
              <label
                htmlFor="otpEnteredConfirmed"
                className="cobalt-text-sm cobalt-text-gray-700"
              >
                I've entered the OTP in Practice Fusion and logged in
                successfully
              </label>
            </div>
          </>
        )}
        {entityName === 'Credible' && (
          <input
            type="text"
            name="domain"
            placeholder="Domain"
            value={credentials.domain}
            onChange={handleInputChange}
            className="cobalt-mb-2 cobalt-p-2 cobalt-border cobalt-rounded cobalt-w-full"
          />
        )}
        {requiresNextechAccountNumber && (
          <input
            type="text"
            name="accountNumber"
            placeholder="Account Number"
            value={credentials.accountNumber}
            onChange={handleInputChange}
            className="cobalt-mb-2 cobalt-p-2 cobalt-border cobalt-rounded cobalt-w-full"
          />
        )}
        {usernameBasedEmrs.includes(entityName) ? (
          <input
            type="text"
            name="username"
            placeholder="Username"
            value={credentials.username}
            onChange={handleInputChange}
            className="cobalt-mb-2 cobalt-p-2 cobalt-border cobalt-rounded cobalt-w-full"
          />
        ) : (
          <input
            type="text"
            name="email"
            placeholder="Email"
            value={credentials.email}
            onChange={handleInputChange}
            onBlur={handleEmailBlur}
            className={`cobalt-mb-2 cobalt-p-2 cobalt-border cobalt-rounded cobalt-w-full ${!emailValid && touched ? 'cobalt-border-red-500' : ''}`}
          />
        )}
        {!emailValid && touched && (
          <p className="cobalt-text-red-500 cobalt-text-xs cobalt-italic">
            Please enter a valid email.
          </p>
        )}
        <input
          type="password"
          name="password"
          placeholder="Password"
          value={credentials.password}
          onChange={handleInputChange}
          className="cobalt-mb-2 cobalt-p-2 cobalt-border cobalt-rounded cobalt-w-full"
        />
        <button
          disabled={!checkFormValid()}
          onClick={handleSubmission}
          className={`cobalt-p-2 cobalt-w-full cobalt-bg-primary cobalt-text-white cobalt-rounded cobalt-h-12 ${checkFormValid() ? 'hover:cobalt-bg-primary-dark cobalt-transition-colors cobalt-duration-200' : 'disabled:cobalt-opacity-50'}`}
        >
          Submit
        </button>
      </div>
    </>
  );
};

export default LogIn;
